import { useMemo } from "react";
import { hasPermission } from "@theg1group/permissions";
// Inlined from feature-kit (kept byte-for-byte identical) so rbac-ui does NOT
// take a runtime dependency on @theg1group/feature-kit: consumers pin different
// feature-kit versions and the older ones don't export these, which broke the
// build for SPAs on an older feature-kit (affiliates/news-api/apps) after 1.3.0.
function pageIdFromPath(path) {
    const trimmed = path.replace(/^\/+/, "").replace(/\/+$/, "");
    const first = trimmed.split("/")[0] ?? "";
    if (!first || first.startsWith(":"))
        return "index";
    return first.toLowerCase();
}
function qualifyPageId(app, pageId) {
    return `${app}.${pageId}`;
}
import { useAuth } from "./auth-context.js";
import { usePagesRegistry } from "./pages-registry.js";
/** Resolve a path to its registry page: exact match first, then longest-prefix. */
function matchPath(pages, path) {
    let exact = null;
    let bestPrefix = null;
    let bestPrefixLen = 0;
    for (const p of pages) {
        if (p.path === path) {
            exact = p;
        }
        else if (path.startsWith(`${p.path}/`) && p.path.length > bestPrefixLen) {
            bestPrefix = p;
            bestPrefixLen = p.path.length;
        }
    }
    return exact ?? bestPrefix;
}
/**
 * Resolve the registry page that owns a route path.
 *
 * Page paths are NOT globally unique: the same `/global-entities`,
 * `/sites`, `/audit-log`, … are registered by several apps (e.g.
 * `console.global-entities` AND `review.global-entities`). When the host SPA
 * declares its `appId`, scope the match to that app first so route gating
 * checks the resource the staff matrix actually grants for this app
 * (`console.global-entities`), not whichever same-path row happens to sort
 * last in the registry array (which would check `review.global-entities` and
 * wrongly deny a console-only manager). Falls back to a cross-app match when
 * `appId` is unset or the app owns no page at that path.
 */
function findPageForPath(pages, path, appId) {
    if (appId) {
        const scoped = matchPath(pages.filter((p) => p.app === appId), path);
        if (scoped)
            return scoped;
    }
    return matchPath(pages, path);
}
export function usePermissions() {
    const { user } = useAuth();
    const { pages, isReady, appId } = usePagesRegistry();
    const perms = user?.permissions ?? [];
    const apps = user?.appAccess ?? [];
    return useMemo(() => {
        const can = (resource, action) => hasPermission(perms, resource, action);
        return {
            can,
            canView: (r) => can(r, "view"),
            canCreate: (r) => can(r, "create"),
            canEdit: (r) => can(r, "edit"),
            canDelete: (r) => can(r, "delete"),
            canViewRoute: (path) => {
                if (!isReady)
                    return true;
                const page = findPageForPath(pages, path, appId);
                if (page)
                    return can(page.id, "view");
                // Not in the pages-registry — typically a client-only page (apiPaths:[])
                // that the backend never gates. Previously this returned true, so such
                // pages showed in the sidebar to EVERYONE, permissionless (e.g. a
                // zero-grant user saw "Currency Rates" / "Sessions"). Instead derive the
                // page id from the path and require `view` on it, like any real page.
                // `index` (the app root / dashboard) is exempt — it has no page-level
                // permission and must stay visible.
                const pageId = pageIdFromPath(path);
                // `index` (app root/dashboard) has no page permission; and without an
                // appId we can't qualify the resource — keep those visible.
                if (pageId === "index" || !appId)
                    return true;
                return can(qualifyPageId(appId, pageId), "view");
            },
            hasAppAccess: (app) => {
                if (user?.role === "admin")
                    return true;
                if (apps.includes("*"))
                    return true;
                return apps.includes(app);
            },
        };
        // `perms`/`apps` are derived from `user`; depend on `user` so memoization
        // invalidates correctly when the same array reference appears across renders.
    }, [user, perms, apps, pages, isReady, appId]);
}
//# sourceMappingURL=use-permissions.js.map